import { apiQuery, atsApiQuery } from '..';

export const jobBoardsApi = apiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      getAccountIntegrations: builder.query({
        providesTags: ['GetJobBoardsIntegrations'],
        transformResponse: (response) => response?.data,
        query: ({ scope }) => {
          return {
            method: 'get',
            url: `/account_integrations?scope=${scope}`,
          };
        },
        staleTime: 0,
      }),

      getJobBoardById: builder.query({
        transformResponse: (response) => response?.data,
        query: ({ id }) => {
          return {
            method: 'get',
            url: `/account_integrations/${id}/edit`,
          };
        },
        staleTime: 0,
      }),

      createIntegrations: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ payload }) => {
          return {
            method: 'post',
            url: `account_integrations`,
            data: payload,
          };
        },
        invalidatesTags: (result) => {
          if (result) {
            return [{ type: 'GetJobBoardsIntegrations' }];
          }
          return [];
        },
      }),

      updateIntegrations: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ payload, id }) => {
          return {
            method: 'patch',
            url: `account_integrations/${id}`,
            data: payload,
          };
        },
        invalidatesTags: (result) => {
          if (result) {
            return [{ type: 'GetJobBoardsIntegrations' }];
          }
          return [];
        },
      }),

      deleteIntegrations: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ id }) => {
          return {
            method: 'delete',
            url: `account_integrations/${id}`,
          };
        },
        invalidatesTags: (result) => {
          if (result) {
            return [{ type: 'GetJobBoardsIntegrations' }];
          }
          return [];
        },
      }),

      // Account/org general settings — exposes display_name (the "Company name"
      // configured in General Settings), used to seed new LinkedIn postings.
      getAccount: builder.query({
        transformResponse: (response) => response?.data,
        query: () => {
          return {
            method: 'get',
            url: '/account',
          };
        },
        staleTime: 0,
      }),
    };
  },
  overrideExisting: true,
});

export const currentJobBoardsApi = atsApiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      getCurrentJobBoards: builder.query({
        // Id-scoped so a single posting's mutation doesn't refetch every other board's detail
        // query (see getCurrentJobBoardByType below).
        providesTags: [{ type: 'GetCurrentJobBoards', id: 'LIST' }],
        transformResponse: (response) => response?.data,
        query: ({ id }) => {
          return {
            method: 'get',
            url: `/ats_jobs/${id}/job_board_postings`,
          };
        },
        staleTime: 0,
      }),

      getCurrentJobBoardByType: builder.query({
        transformResponse: (response) => response?.data,
        // Tag the board fetch (not the live status poll) so create/update refresh the drawer's
        // board too; otherwise a same-mount reopen shows a stale title/job_type/isEditing.
        // Scoped to this posting: an untagged-id tag would put every mounted detail query on the
        // list's tag, so publishing to Indeed would refetch the LinkedIn and JobTarget postings.
        providesTags: (result, error, arg) =>
          arg?.isStatus
            ? []
            : [{ type: 'GetCurrentJobBoards', id: `${arg?.id}:${arg?.type}` }],
        query: ({ id, type, isExternal = false, isStatus = false }) => {
          let url = `ats_jobs/${id}/job_board_postings/${type}`;
          if (isExternal) {
            url += '/external';
          }
          if (isStatus) {
            url += '/status';
          }
          return {
            method: 'get',
            url,
          };
        },
        staleTime: 0,
      }),

      createJobBoard: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ id, payload }) => {
          return {
            method: 'post',
            url: `ats_jobs/${id}/job_board_postings`,
            data: payload,
          };
        },
        invalidatesTags: (result, error, arg) => {
          if (result) {
            return [
              { type: 'GetCurrentJobBoards', id: 'LIST' },
              {
                type: 'GetCurrentJobBoards',
                id: `${arg?.id}:${arg?.payload?.job_board_type}`,
              },
            ];
          }
          return [];
        },
      }),

      deleteJobBoard: builder.mutation({
        transformResponse: (response) => response?.data,
        query: ({ id, type }) => {
          return {
            method: 'delete',
            url: `ats_jobs/${id}/job_board_postings/${type}`,
          };
        },
        invalidatesTags: (result, error, arg) => {
          if (result) {
            return [
              { type: 'GetCurrentJobBoards', id: 'LIST' },
              { type: 'GetCurrentJobBoards', id: `${arg?.id}:${arg?.type}` },
            ];
          }
          return [];
        },
      }),
    };
  },
  overrideExisting: true,
});

export const {
  useGetAccountIntegrationsQuery,
  useLazyGetAccountIntegrationsQuery,
  useLazyGetJobBoardByIdQuery,
  useCreateIntegrationsMutation,
  useDeleteIntegrationsMutation,
  useUpdateIntegrationsMutation,
  useGetAccountQuery,
} = jobBoardsApi;

export const {
  useGetCurrentJobBoardsQuery,
  useLazyGetCurrentJobBoardsQuery,
  useCreateJobBoardMutation,
  useDeleteJobBoardMutation,
  useLazyGetCurrentJobBoardByTypeQuery,
  useGetCurrentJobBoardByTypeQuery,
} = currentJobBoardsApi;
